import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiAlertTriangle,
  FiTrash2,
  FiCheck,
  FiX,
  FiRefreshCw,
  FiDatabase,
  FiCalendar,
  FiUser,
  FiClock,
  FiFileText
} from 'react-icons/fi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import ConfirmationModal from './ConfirmationModal';
import {
  findDuplicateDORs,
  migrateDORsWithDateOnly,
  removeDuplicateDORs,
  generateDuplicateReport
} from '../utils/migrateDORs';
import { format } from 'date-fns';

const ManageDuplicateDORs = () => {
  const { role } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [report, setReport] = useState(null);
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Helper function to safely format dates
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      // Handle Firestore Timestamp
      if (date && typeof date === 'object' && 'seconds' in date) {
        return format(new Date(date.seconds * 1000), 'MMM d, yyyy h:mm a');
      }
      // Handle toDate method (Firestore Timestamp)
      if (date && typeof date.toDate === 'function') {
        return format(date.toDate(), 'MMM d, yyyy h:mm a');
      }
      // Handle Date object or string
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return 'Invalid Date';
      return format(d, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Invalid Date';
    }
  };

  const loadDuplicates = async () => {
    try {
      setLoading(true);
      const result = await generateDuplicateReport();
      
      if (result.success) {
        setReport(result);
        const duplicateData = await findDuplicateDORs();
        setDuplicates(duplicateData);
        
        const duplicateCount = result.totalDuplicateSets || 0;
        if (duplicateCount === 0) {
          toast.success('No duplicate DORs found in the system!');
        } else {
          toast.info(`Found ${duplicateCount} sets of duplicate DORs`);
        }
      } else {
        toast.error(`Failed to load duplicates: ${result.error}`);
      }
    } catch (error) {
      console.error('Error loading duplicates:', error);
      toast.error('Failed to load duplicate DORs');
    } finally {
      setLoading(false);
    }
  };

  const handleMigration = async () => {
    setConfirmModal({
      isOpen: true,
      title: 'Migrate DORs',
      message: 'This will add the dateOnly field to all DORs that are missing it. This helps prevent future duplicates. Continue?',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        setMigrating(true);
        
        try {
          const result = await migrateDORsWithDateOnly();
          
          if (result.success) {
            toast.success(`Migration completed! Updated ${result.updated} DORs`);
            if (result.errors > 0) {
              toast.warning(`${result.errors} DORs had errors and were skipped`);
              console.error('Migration errors:', result.errorDetails);
            }
            // Reload duplicates after migration
            await loadDuplicates();
          } else {
            toast.error(`Migration failed: ${result.error}`);
          }
        } catch (error) {
          console.error('Migration error:', error);
          toast.error('Migration failed');
        } finally {
          setMigrating(false);
        }
      },
    });
  };

  const handleRemoveDuplicate = (duplicate) => {
    setSelectedDuplicate(duplicate);
    setConfirmModal({
      isOpen: true,
      title: 'Remove Duplicate DORs',
      message: `This will keep the oldest DOR for ${duplicate.traineeName} on ${duplicate.date} and delete ${duplicate.count - 1} duplicate(s). Continue?`,
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        
        try {
          const result = await removeDuplicateDORs(duplicate);
          
          if (result.success) {
            toast.success(`Removed ${result.deleted.length} duplicate DOR(s)`);
            // Reload duplicates
            await loadDuplicates();
          } else {
            toast.error(`Failed to remove duplicates: ${result.error}`);
          }
        } catch (error) {
          console.error('Error removing duplicates:', error);
          toast.error('Failed to remove duplicates');
        } finally {
          setSelectedDuplicate(null);
        }
      },
    });
  };

  useEffect(() => {
    if (role === 'admin') {
      loadDuplicates();
    }
  }, [role]);

  // Only admins can access this component
  if (role !== 'admin') {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="p-6 flex items-center justify-center h-full"
      >
        <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg shadow-md flex items-center">
          <FiAlertTriangle className="mr-2 flex-shrink-0" size={20} />
          <span>Access Denied - Admin privileges required</span>
        </div>
      </motion.div>
    );
  }

  const inputStyle = `
    w-full 
    p-3 
    rounded-md 
    bg-secondary-light/30 
    dark:bg-secondary-dark 
    border 
    border-secondary-light 
    dark:border-secondary-dark 
    focus:ring-2 
    focus:ring-accent-light 
    dark:focus:ring-accent-dark 
    focus:border-transparent 
    outline-none 
    transition-all 
    duration-200
    text-text-light
    dark:text-white
  `;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="p-4 sm:p-6 md:p-8 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark transition-colors duration-300"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-6"
          >
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3 mb-2">
              <FiDatabase className="text-accent-light dark:text-accent-dark" />
              Manage Duplicate DORs
            </h1>
            <p className="text-sm text-text-light/70 dark:text-text-dark/70">
              Review and manage duplicate Daily Observation Reports in the system
            </p>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-6 flex flex-wrap gap-3"
          >
            <button
              onClick={loadDuplicates}
              disabled={loading || migrating}
              className="px-4 py-2 bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark rounded-lg transition-all duration-300 flex items-center gap-2 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <button
              onClick={handleMigration}
              disabled={loading || migrating}
              className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg transition-all duration-300 flex items-center gap-2 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {migrating ? (
                <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
              ) : (
                <FiDatabase className="w-4 h-4" />
              )}
              {migrating ? 'Migrating...' : 'Run Migration'}
            </button>
          </motion.div>

          {/* Summary Stats */}
          {report && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4"
            >
              <div className="bg-primary-light dark:bg-primary-dark p-4 rounded-lg border border-secondary-light dark:border-secondary-dark">
                <div className="flex items-center gap-2 mb-2">
                  <FiFileText className="text-accent-light dark:text-accent-dark" />
                  <span className="text-sm font-medium">Total Duplicate Sets</span>
                </div>
                <p className="text-2xl font-bold">{report.totalDuplicateSets || 0}</p>
              </div>
              
              <div className="bg-primary-light dark:bg-primary-dark p-4 rounded-lg border border-secondary-light dark:border-secondary-dark">
                <div className="flex items-center gap-2 mb-2">
                  <FiAlertTriangle className="text-yellow-600 dark:text-yellow-400" />
                  <span className="text-sm font-medium">Duplicate DORs</span>
                </div>
                <p className="text-2xl font-bold">{report.totalDuplicateDORs || 0}</p>
              </div>
              
              <div className="bg-primary-light dark:bg-primary-dark p-4 rounded-lg border border-secondary-light dark:border-secondary-dark">
                <div className="flex items-center gap-2 mb-2">
                  <FiCheck className="text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium">Status</span>
                </div>
                <p className="text-sm font-medium">
                  {(report.totalDuplicateSets || 0) === 0 ? (
                    <span className="text-green-600 dark:text-green-400">No Duplicates</span>
                  ) : (
                    <span className="text-yellow-600 dark:text-yellow-400">Action Required</span>
                  )}
                </p>
              </div>
            </motion.div>
          )}

          {/* Duplicates List */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="bg-primary-light dark:bg-primary-dark rounded-lg shadow-lg border border-secondary-light dark:border-secondary-dark overflow-hidden"
          >
            {loading ? (
              <div className="p-8 flex justify-center items-center">
                <AiOutlineLoading3Quarters className="w-8 h-8 animate-spin text-accent-light dark:text-accent-dark" />
              </div>
            ) : duplicates.length === 0 ? (
              <div className="p-8 text-center">
                <FiCheck className="w-12 h-12 mx-auto mb-4 text-green-600 dark:text-green-400" />
                <p className="text-lg font-medium">No duplicate DORs found!</p>
                <p className="text-sm text-text-light/70 dark:text-text-dark/70 mt-2">
                  All DORs in the system are unique.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-secondary-light dark:divide-secondary-dark">
                {duplicates.map((duplicate, index) => (
                  <motion.div
                    key={duplicate.key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="p-4 hover:bg-secondary-light/10 dark:hover:bg-secondary-dark/10 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          <FiUser className="text-accent-light dark:text-accent-dark" />
                          {duplicate.traineeName}
                        </h3>
                        <p className="text-sm text-text-light/70 dark:text-text-dark/70 flex items-center gap-2 mt-1">
                          <FiCalendar className="w-3 h-3" />
                          {duplicate.date}
                          <span className="mx-2">•</span>
                          <FiAlertTriangle className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />
                          {duplicate.count} duplicates
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveDuplicate(duplicate)}
                        disabled={selectedDuplicate?.key === duplicate.key}
                        className="px-3 py-1 bg-red-600 dark:bg-red-700 text-white rounded-md text-sm flex items-center gap-1 hover:bg-red-700 dark:hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {selectedDuplicate?.key === duplicate.key ? (
                          <AiOutlineLoading3Quarters className="w-3 h-3 animate-spin" />
                        ) : (
                          <FiTrash2 className="w-3 h-3" />
                        )}
                        Remove Duplicates
                      </button>
                    </div>
                    
                    {/* DOR Details */}
                    <div className="space-y-2 mt-3">
                      {duplicate.dors.map((dor, dorIndex) => (
                        <div
                          key={dor.id}
                          className={`text-sm p-2 rounded ${
                            dorIndex === 0
                              ? 'bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700'
                              : 'bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">
                              {dor.reportId || 'No Report ID'}
                              {dorIndex === 0 && (
                                <span className="ml-2 text-xs text-green-700 dark:text-green-400">
                                  (Will be kept)
                                </span>
                              )}
                            </span>
                            <span className="text-xs text-text-light/60 dark:text-text-dark/60">
                              {dor.hasDateOnly ? (
                                <FiCheck className="inline w-3 h-3 text-green-600 dark:text-green-400 mr-1" />
                              ) : (
                                <FiX className="inline w-3 h-3 text-red-600 dark:text-red-400 mr-1" />
                              )}
                              dateOnly field
                            </span>
                          </div>
                          <div className="text-xs text-text-light/70 dark:text-text-dark/70 mt-1">
                            <span className="flex items-center gap-1">
                              <FiUser className="w-3 h-3" />
                              Trainer: {dor.trainerName}
                            </span>
                            <span className="flex items-center gap-1 mt-1">
                              <FiClock className="w-3 h-3" />
                              Created: {formatDate(dor.createdAt)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Proceed"
        cancelText="Cancel"
      />
    </>
  );
};

export default ManageDuplicateDORs;